import { useMemo } from 'react';
import { toast } from '@invento/ui/components/toast';
import type {
  ToastAction,
  ToastId,
  ToastOptions,
  ToastPromiseOptions,
  ToastType,
  ToastUpdateOptions,
} from '@invento/ui/components/toast';
import type { ReactNode } from 'react';

export interface NotificationScope {
  readonly epoch: number;
}

type NotificationOptions = Omit<ToastOptions, 'title' | 'description' | 'action' | 'cancel'> & {
  action?: ToastAction;
  cancel?: ToastAction;
};

export interface ScopedNotifications {
  show: (title: ReactNode, description?: ReactNode, options?: NotificationOptions) => ToastId | undefined;
  success: (title: ReactNode, description?: ReactNode, options?: NotificationOptions) => ToastId | undefined;
  error: (title: ReactNode, description?: ReactNode, options?: NotificationOptions) => ToastId | undefined;
  warning: (title: ReactNode, description?: ReactNode, options?: NotificationOptions) => ToastId | undefined;
  info: (title: ReactNode, description?: ReactNode, options?: NotificationOptions) => ToastId | undefined;
  loading: (title: ReactNode, description?: ReactNode, options?: NotificationOptions) => ToastId | undefined;
  update: (id: ToastId, options: ToastUpdateOptions) => ToastId | undefined;
  promise: <T>(promise: Promise<T>, options: ToastPromiseOptions<T>) => ToastId | undefined;
  dismiss: (id?: ToastId) => void;
}

let notificationEpoch = 0;
const sessionResetters = new Set<() => void>();

/** Starts an identity boundary and prevents an earlier identity's notices leaking through. */
export function beginNotificationSession(): NotificationScope {
  notificationEpoch += 1;
  toast.dismiss();
  return { epoch: notificationEpoch };
}

/** Invalidates every existing scoped callback, including loading/promise completions. */
export function clearNotificationSession(): void {
  notificationEpoch += 1;
  toast.dismiss();
  for (const reset of sessionResetters) reset();
}

export function getNotificationSessionEpoch(): number {
  return notificationEpoch;
}

export function createNotificationScope(): NotificationScope {
  return { epoch: notificationEpoch };
}

export function isNotificationScopeActive(scope: NotificationScope): boolean {
  return scope.epoch === notificationEpoch;
}

/** Lets an owning cross-cutting concern reset per-session state without import cycles. */
export function registerNotificationSessionResetter(reset: () => void): () => void {
  sessionResetters.add(reset);
  return () => sessionResetters.delete(reset);
}

function actionForScope(scope: NotificationScope, action?: ToastAction): ToastAction | undefined {
  if (!action) return undefined;
  return {
    label: action.label,
    onClick: (event) => {
      if (!isNotificationScopeActive(scope)) {
        event.preventDefault();
        return;
      }
      action.onClick(event);
    },
  };
}

function scopedId(scope: NotificationScope, id?: ToastId): ToastId | undefined {
  if (id === undefined) return undefined;
  const prefix = `notification-${scope.epoch}-`;
  return String(id).startsWith(prefix) ? id : `${prefix}${id}`;
}

function toOptions(
  scope: NotificationScope,
  description: ReactNode | undefined,
  options: NotificationOptions | undefined,
): ToastOptions {
  return {
    ...options,
    description,
    id: scopedId(scope, options?.id),
    action: actionForScope(scope, options?.action),
    cancel: actionForScope(scope, options?.cancel),
  };
}

export function createScopedNotifications(scope: NotificationScope): ScopedNotifications {
  const emit = (
    type: ToastType,
    title: ReactNode,
    description?: ReactNode,
    options?: NotificationOptions,
  ): ToastId | undefined => {
    if (!isNotificationScopeActive(scope)) return undefined;
    const toastOptions = { title, ...toOptions(scope, description, options) };
    switch (type) {
      case 'success': return toast.success(toastOptions);
      case 'error': return toast.error(toastOptions);
      case 'warning': return toast.warning(toastOptions);
      case 'info': return toast.info(toastOptions);
      case 'loading': return toast.loading(toastOptions);
      default: return toast.show(toastOptions);
    }
  };

  return {
    show: (title, description, options) => emit('default', title, description, options),
    success: (title, description, options) => emit('success', title, description, options),
    error: (title, description, options) => emit('error', title, description, options),
    warning: (title, description, options) => emit('warning', title, description, options),
    info: (title, description, options) => emit('info', title, description, options),
    loading: (title, description, options) => emit('loading', title, description, options),
    update: (id, options) => {
      if (!isNotificationScopeActive(scope)) return undefined;
      const { action, cancel, ...updateOptions } = options;
      return toast.update(scopedId(scope, id) ?? id, {
        ...updateOptions,
        ...(action ? { action: actionForScope(scope, action) } : {}),
        ...(cancel ? { cancel: actionForScope(scope, cancel) } : {}),
      });
    },
    promise: <T,>(promise: Promise<T>, options: ToastPromiseOptions<T>) => {
      const id = emit('loading', options.loading, options.description, { id: options.id, duration: 0 });
      if (id === undefined) return undefined;

      void promise.then(
        (data) => {
          if (isNotificationScopeActive(scope)) {
            toast.success({
              title: typeof options.success === 'function' ? options.success(data) : options.success,
              description: options.description,
              id,
            });
          }
        },
        (error) => {
          if (isNotificationScopeActive(scope)) {
            toast.error({
              title: typeof options.error === 'function' ? options.error(error) : options.error,
              description: options.description,
              id,
            });
          }
        },
      );
      return id;
    },
    dismiss: (id) => {
      if (!isNotificationScopeActive(scope)) return;
      toast.dismiss(id === undefined ? undefined : scopedId(scope, id));
    },
  };
}

/**
 * Capture the current identity epoch in the render that owns an async action.
 * A later login creates a new epoch, so the old closure cannot notify the new
 * identity when its request finally settles.
 */
export function useNotifications(): ScopedNotifications {
  const scope = createNotificationScope();
  return useMemo(() => createScopedNotifications(scope), [scope.epoch]);
}

/** Test-only reset; application session transitions must use the public APIs. */
export function __resetNotificationSessionForTests(): void {
  notificationEpoch = 0;
  toast.dismiss();
  for (const reset of sessionResetters) reset();
}
